import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { HitmasterLogo } from "@/components/HitmasterLogo";
import { Equalizer } from "@/components/Equalizer";
import { BackToLobby } from "@/components/BackToLobby";
import { usePersistentPlayer } from "@/components/PersistentPlayer";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { HowToPlay } from "@/components/HowToPlay";
import { Trash2, Search, Copy, ChevronDown, RotateCcw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HelpCircle, Lock } from "lucide-react";
import { getTheme, getStoredStyle, bgImageStyle } from "@/lib/themes";
import { useLang } from "@/i18n";

// Metadata for each game type — used by info modals (⌕)
const GAME_TYPE_INFO = {
  1: {
    title: "Raad artiest, lied en jaar",
    color: "#4a7cab",
    modeLabel: "Geschikt voor bordspel & phone",
    points: [
      { label: "Artiest correct", value: "1 pt" },
      { label: "Titel correct", value: "3 pt" },
      { label: "Jaartal exact", value: "3 pt" },
      { label: "Alles correct (bonus)", value: "+3 pt" },
      { label: "Foute challenge (straf)", value: "−3 pt" },
    ],
    description: "Om de beurt (willekeurige start). De speler aan de beurt raadt of kiest 'Weet ik niet'. Daarna krijgen de anderen 5 sec om te challengen. Alles goed = 10 pt. Let op: een FOUTE challenge kost de uitdager −3 punten.",
  },
  2: {
    title: "Wie is het snelst — 25 sec",
    color: "#5094b7",
    modeLabel: "Alleen phone",
    points: [
      { label: "Artiest correct", value: "2 pt" },
      { label: "Titel correct", value: "2 pt" },
      { label: "Combo bonus (beide)", value: "+1 pt" },
    ],
    description: "25 seconden fragment. Wie het eerst op de groene knop drukt mag antwoorden. Anderen kunnen met de rode challenge-knop 1× per ronde uitdagen. Max 5 pt.",
  },
  3: {
    title: "Raad de artiest — Photo Mosaic",
    color: "#7c3aed",
    modeLabel: "Alleen phone",
    points: [
      { label: "Artiest correct", value: "3 pt" },
      { label: "+ Album titel", value: "5 pt (max)" },
    ],
    description: "16 puzzelblokken van de artiestenfoto onthullen langzaam. Wie het eerst buzzt raadt de artiest (album als bonus). Max 5 pt.",
  },
  4: {
    title: "Videoclip op je telefoon",
    color: "#0f766e",
    modeLabel: "Alleen phone",
    points: [
      { label: "Artiest correct", value: "1 pt" },
      { label: "Titel correct", value: "3 pt" },
      { label: "Jaartal correct", value: "3 pt" },
      { label: "Alles correct (bonus)", value: "+3 pt" },
    ],
    description: "De videoclip speelt op je eigen telefoon. Wie het eerst op de groene knop drukt raadt artiest + titel en kiest het juiste jaartal uit 3 opties. Alles goed = 10 punten.",
  },
};

export default function HostSetup() {
  const nav = useNavigate();
  const { t: tr } = useLang();
  const player = usePersistentPlayer();
  const [playlists, setPlaylists] = useState([]);
  const [loadingPl, setLoadingPl] = useState(true);
  const [selectedPlaylistIds, setSelectedPlaylistIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [gameType, setGameType] = useState(1);
  const [gameTypes, setGameTypes] = useState([1]); // multi-select
  const [audioSource, setAudioSource] = useState("host"); // "host" | "players"
  const [rounds, setRounds] = useState(10);
  const [mode, setMode] = useState("phone");
  const [roomCode, setRoomCode] = useState(null);
  const [hostId, setHostId] = useState(null);
  const [players, setPlayers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addInput, setAddInput] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null); // {id, title}
  const [infoModalGt, setInfoModalGt] = useState(null); // 1..4 | null
  const [previewStats, setPreviewStats] = useState(null); // {playlistCount, totalRaw, unique, duplicates, played, remaining}
  const [pendingReset, setPendingReset] = useState(false);
  const [savedSessions, setSavedSessions] = useState([]);
  const [resumingId, setResumingId] = useState(null);

  const confirmResetPlayed = async () => {
    setPendingReset(false);
    if (selectedPlaylistIds.length === 0) return;
    try {
      const { data } = await api.post("/playlists/reset-played", { playlistIds: selectedPlaylistIds });
      toast.success(`Reset voltooid — ${data.resetCount} playlist${data.resetCount === 1 ? "" : "s"}`);
      // Refresh preview stats + playlist list (played counts)
      const [statsRes] = await Promise.all([
        api.post("/playlists/preview-stats", { playlistIds: selectedPlaylistIds }),
        loadPlaylists(),
      ]);
      setPreviewStats(statsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset mislukt");
    }
  };

  const [loadError, setLoadError] = useState(null);
  const loadSavedSessions = async () => {
    try {
      const { data } = await api.get("/saved-sessions");
      setSavedSessions(data);
    } catch { /* non-fatal: resume list is optional */ }
  };
  const resumeSession = async (s) => {
    setResumingId(s.sessionId);
    try {
      const { data } = await api.post(`/saved-sessions/${s.sessionId}/resume`);
      toast.success(`Sessie hervat: "${s.name}" ✓`);
      nav(`/host/${data.code}`);
    } catch {
      toast.error("Hervatten mislukt");
      setResumingId(null);
    }
  };
  const deleteSession = async (s) => {
    try {
      await api.delete(`/saved-sessions/${s.sessionId}`);
      setSavedSessions((prev) => prev.filter((x) => x.sessionId !== s.sessionId));
      toast.success("Opgeslagen sessie verwijderd");
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };
  const loadPlaylists = async () => {
    setLoadingPl(true);
    setLoadError(null);
    try {
      const { data } = await api.get("/playlists");
      setPlaylists(data);
      setSelectedPlaylistIds((prev) => prev.length ? prev : (data[0] ? [data[0].playlistId] : []));
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || "Kon playlists niet laden");
      toast.error("Playlists laden mislukt — probeer opnieuw");
    } finally {
      setLoadingPl(false);
    }
  };

  const togglePlaylistSelection = (id) => {
    setSelectedPlaylistIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 6) { toast.error("Max 6 playlists selected"); return prev; }
      return [...prev, id];
    });
  };

  const deletePlaylist = (id, title) => {
    // Open AlertDialog for confirmation (works reliably on mobile/desktop, unlike window.confirm)
    setPendingDelete({ id, title });
  };

  const confirmDeletePlaylist = async () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;
    setPendingDelete(null);
    try {
      await api.delete(`/playlists/${id}`);
      setSelectedPlaylistIds((prev) => prev.filter((x) => x !== id));
      setPlaylists((prev) => prev.filter((p) => p.playlistId !== id));
      toast.success("Playlist verwijderd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const importDefaultPlaylist = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/playlists/import", { query: "Top 2000 - NPO Radio 2- 2025" });
      toast.success(`Imported ${data.title} (${data.count} songs)`);
      await loadPlaylists();
      setSelectedPlaylistIds((prev) => prev.includes(data.playlistId) ? prev : [...prev, data.playlistId].slice(0, 6));
    } catch (e) {
      toast.error("Failed to import playlist. Check YouTube API key.");
    } finally {
      setBusy(false);
    }
  };

  const extractPlaylistId = (raw) => {
    // supports full URL (?list=PLxxx or &list=PLxxx), or a bare id starting with PL/UU/OLAK/RD
    try {
      const url = new URL(raw);
      const p = url.searchParams.get("list");
      if (p) return p;
    } catch {}
    const m = raw.match(/[?&]list=([^&\s]+)/);
    if (m) return m[1];
    if (/^(PL|UU|OLAK|RD|LL|FL)[A-Za-z0-9_-]{10,}/.test(raw.trim())) return raw.trim();
    return null;
  };

  const runPlaylistSearch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) return toast.error(tr("setup.searchMin"));
    setSearching(true);
    setSearchDone(false);
    try {
      const { data } = await api.get(`/playlists/search`, { params: { q } });
      setSearchResults(Array.isArray(data) ? data : []);
      setSearchDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const addSearchResult = async (result) => {
    if (playlists.some((p) => p.playlistId === result.playlistId)) {
      return toast(tr("setup.alreadyAdded"), { icon: "✓" });
    }
    if (playlists.length >= 6) return toast.error("Max 6 playlists");
    setBusy(true);
    try {
      const { data } = await api.post("/playlists/import", { playlistId: result.playlistId });
      toast.success(`${data.title} (${data.count})`);
      await loadPlaylists();
      setSelectedPlaylistIds((prev) => prev.includes(data.playlistId) ? prev : [...prev, data.playlistId].slice(0, 6));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const importCustomPlaylist = async () => {
    const val = addInput.trim();
    if (!val) return toast.error("Enter a URL, ID or search text");
    if (playlists.length >= 6) return toast.error("Max 6 playlists");
    setBusy(true);
    try {
      const pid = extractPlaylistId(val);
      const body = pid ? { playlistId: pid } : { query: val };
      const { data } = await api.post("/playlists/import", body);
      toast.success(`Imported ${data.title} (${data.count} songs)`);
      await loadPlaylists();
      setSelectedPlaylistIds((prev) => prev.includes(data.playlistId) ? prev : [...prev, data.playlistId].slice(0, 6));
      setAddInput("");
      setShowAddForm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to import");
    } finally {
      setBusy(false);
    }
  };

  const createRoom = async () => {
    const missing = collectValidationErrors();
    if (missing.length) { setValidationModal({ missing }); return null; }
    const { data } = await api.post("/rooms", { hostName: "Host", style: getStoredStyle() });
    setRoomCode(data.code);
    setHostId(data.hostId);
    toast.success(`Room ${data.code} created`);
    return data.code;
  };

  // poll players in lobby
  useEffect(() => {
    if (!roomCode) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/rooms/${roomCode}`);
        setPlayers(data.players || []);
      } catch {}
    }, 1500);
    return () => clearInterval(t);
  }, [roomCode]);

  useEffect(() => { loadPlaylists(); loadSavedSessions(); }, []); // eslint-disable-line

  // Fetch dedup preview stats whenever the playlist selection changes
  useEffect(() => {
    if (selectedPlaylistIds.length === 0) { setPreviewStats(null); return; }
    let cancelled = false;
    api.post("/playlists/preview-stats", { playlistIds: selectedPlaylistIds })
      .then(({ data }) => { if (!cancelled) setPreviewStats(data); })
      .catch(() => { if (!cancelled) setPreviewStats(null); });
    return () => { cancelled = true; };
  }, [selectedPlaylistIds]);

  // if board mode, force to only game type 1 (2 and 3 are phone-only)
  useEffect(() => {
    if (mode === "board") {
      setGameTypes((gt) => {
        const filtered = gt.filter((t) => t === 1);
        return filtered.length ? filtered : [1];
      });
    }
  }, [mode]); // eslint-disable-line

  const toggleGameType = (t) => {
    setGameTypes((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      return [...prev, t].sort();
    });
  };

  // Validation modal state ({title, missing:[]}) shown before create/start
  const [validationModal, setValidationModal] = useState(null);
  const [confirmStart, setConfirmStart] = useState(false);

  const collectValidationErrors = () => {
    const missing = [];
    if (selectedPlaylistIds.length === 0) missing.push("playlist");
    if (gameTypes.length === 0) missing.push("gametype");
    return missing;
  };

  const startGame = async () => {
    const missing = collectValidationErrors();
    if (missing.length) { setValidationModal({ missing }); return; }
    let code = roomCode;
    if (!code) code = await createRoom();
    if (!code) return;
    setBusy(true);

    // Prime audio context in the SAME user gesture as the click, so YouTube autoplay-with-sound is allowed.
    try { player?.unmute?.(); } catch {}
    try { player?.play?.(); } catch {}

    try {
      const res = await api.post(`/rooms/${code}/start`, {
        gameType: gameTypes[0],
        gameTypes: gameTypes,
        rounds,
        playlistId: selectedPlaylistIds[0],
        playlistIds: selectedPlaylistIds,
        mode,
        audioSource,
      });
      const firstVid = res?.data?.song?.videoId;
      const firstGt = res?.data?.gameType;
      // Skip priming the persistent YT player when the host is intentionally silent:
      //  - G3: custom cinematic audio only
      //  - G4: video on players' phones
      //  - audioSource=players: music on player devices
      const hostSilent = firstGt === 3 || firstGt === 4 || audioSource === "players";
      if (firstVid && player && !hostSilent) {
        try { player.load(firstVid); } catch {}
        try { player.unmute(); } catch {}
      }
      nav(`/host/${code}`);
    } catch (e) {
      toast.error("Failed to start");
    } finally {
      setBusy(false);
    }
  };

  const lobbyTheme = getTheme(getStoredStyle());
  // Before create room = game-config screen (video 1); after create room = "get ready"/lobby (video 2 if theme provides one).
  // Bling & Flava: the host lobby and select-game screens are swapped (per user request).
  const _blingSwap = lobbyTheme.id === "puppy_love";
  const setupBgVideo = roomCode
    ? (_blingSwap ? (lobbyTheme.lobbyVideo || lobbyTheme.getReadyVideo) : (lobbyTheme.getReadyVideo || lobbyTheme.lobbyVideo))
    : (_blingSwap ? (lobbyTheme.getReadyVideo || lobbyTheme.lobbyVideo) : lobbyTheme.lobbyVideo);
  const readyCount = players.filter((p) => p.ready).length;
  return (
    <div className="min-h-screen setup-bg overflow-x-hidden px-3 sm:px-6 relative" style={{ paddingTop: "10px", paddingBottom: "1rem", ...(setupBgVideo ? {} : bgImageStyle(lobbyTheme.host.lobby)) }}>
      {setupBgVideo && (
        <>
          <video
            data-testid="host-lobby-bg-video"
            key={setupBgVideo}
            autoPlay loop muted playsInline preload="auto" aria-hidden
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0, pointerEvents: "none", filter: "saturate(1.22) contrast(1.06) brightness(1.03)" }}
            src={setupBgVideo}
          />
          <div
            aria-hidden
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: "none",
              background: "rgba(14, 18, 40, 0.28)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
            }}
          />
        </>
      )}
      <div className="max-w-5xl mx-auto relative" style={{ zIndex: 1 }}>
        <div className="flex flex-col items-center justify-center mb-2 sm:mb-3 gap-1.5">
          <HitmasterLogo className="h-32 sm:h-44 animate-float" />
          <HowToPlay />
        </div>
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <BackToLobby />
          <div className="text-right shrink-0">
            <div
              className="font-bebas text-white text-xs uppercase tracking-widest"
              style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}
            >
              Host Setup
            </div>
            <Equalizer />
          </div>
        </div>

        {savedSessions.length > 0 && (
          <div className="card-purple min-w-0 mb-3 sm:mb-4" style={{ padding: "12px 14px" }} data-testid="resume-sessions-section">
            <h2 className="font-bebas text-white text-2xl sm:text-3xl tracking-wide mb-2 flex items-center gap-2" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}>
              <RotateCcw className="w-6 h-6 text-gold" strokeWidth={2.6} /> Verdergaan met opgeslagen sessie
            </h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {savedSessions.map((s) => (
                <div
                  key={s.sessionId}
                  data-testid={`saved-session-${s.sessionId}`}
                  className="rounded-xl border-2 border-navy bg-white/85 backdrop-blur-sm px-3 py-2.5 flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-black text-navy text-base truncate flex items-center gap-2">
                      <span className="truncate">{s.name}</span>
                      {s.finished && (
                        <span className="shrink-0 text-[10px] font-black uppercase tracking-widest bg-teal text-white px-1.5 py-0.5 rounded">Voltooid</span>
                      )}
                    </div>
                    <div className="text-xs font-bold text-navy/60">
                      Nummer {s.songsPlayed}/{s.totalSongs} gespeeld · Ronde {s.currentRound}/{s.totalRounds} · {s.playerCount} spelers
                    </div>
                    {s.players?.length > 0 && (
                      <div className="text-[11px] font-bold text-navy/50 truncate">
                        {s.players.map((p) => `${p.name} ${p.score}`).join(" · ")}
                      </div>
                    )}
                  </div>
                  {!s.finished && (
                    <button
                      data-testid={`resume-session-btn-${s.sessionId}`}
                      onClick={() => resumeSession(s)}
                      disabled={resumingId === s.sessionId}
                      className="shrink-0 bg-gold text-navy font-display font-black uppercase text-xs px-3 py-2 rounded-lg border-2 border-navy shadow-[0_3px_0_#1E1B4B] disabled:opacity-60"
                    >
                      {resumingId === s.sessionId ? "…" : "Verdergaan"}
                    </button>
                  )}
                  <button
                    data-testid={`delete-session-btn-${s.sessionId}`}
                    onClick={() => deleteSession(s)}
                    className="shrink-0 text-navy/50 hover:text-coral p-2"
                    title="Verwijderen"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-[44fr_56fr] gap-3 sm:gap-4 items-start">
          {/* Playlists */}
          <div className="card-purple min-w-0 self-start" style={{ padding: "12px 14px 10px 14px" }}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <h2 className="font-bebas text-white text-2xl sm:text-3xl tracking-wide" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}>{tr("setup.selectPlaylists")}</h2>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {playlists.length >= 6 && (
                  <span
                    data-testid="playlists-full-hint"
                    className="text-white text-xs font-bebas uppercase tracking-widest"
                    style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}
                  >
                    {tr("setup.have4")}
                  </span>
                )}
                <button
                  data-testid="add-playlist-btn"
                  onClick={() => setShowAddForm((s) => !s)}
                  disabled={busy || playlists.length >= 6}
                  className="text-white font-bebas uppercase text-xs sm:text-sm px-3 py-1.5 rounded-full border-2 border-navy shadow-[0_4px_14px_rgba(0,0,0,0.25)] hover:translate-y-[-2px] transition-transform disabled:cursor-not-allowed whitespace-nowrap tracking-wider"
                  style={{ backgroundColor: "#e65b7c" }}
                >
                  {showAddForm ? tr("setup.cancel") : tr("setup.addPlaylist")}
                </button>
              </div>
            </div>

            {showAddForm && (
              <div className="mb-4 p-3 rounded-xl border-2 border-navy bg-white/50 backdrop-blur-sm space-y-2">
                <div className="font-bebas text-navy text-lg tracking-wide">{tr("setup.addYt")}</div>
                <div className="text-xs text-navy/70 font-bold">{tr("setup.addYtDesc")}</div>
                <div className="flex gap-2">
                  <input
                    data-testid="add-playlist-input"
                    value={addInput}
                    onChange={(e) => setAddInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") importCustomPlaylist(); }}
                    placeholder="https://youtube.com/playlist?list=PL… or PL… or 80s hits"
                    className="flex-1 bg-white/80 border-2 border-navy rounded-lg px-3 py-2 font-bold text-navy focus:outline-none focus:ring-4 focus:ring-coral/40"
                  />
                  <button
                    data-testid="add-playlist-submit"
                    onClick={importCustomPlaylist}
                    disabled={busy}
                    className="bg-navy text-cream font-bebas uppercase px-4 py-2 rounded-lg tracking-wider disabled:opacity-50"
                  >
                    {tr("setup.import")}
                  </button>
                </div>
              </div>
            )}
            {loadingPl ? (
              <div className="text-navy/60 font-bold">{tr("host.loading")}</div>
            ) : playlists.length === 0 ? (
              <div className="text-navy/60 font-bold">{tr("setup.noPlaylists")}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {playlists.map((p) => (
                  <div
                    key={p.playlistId}
                    className={`w-full flex items-center gap-2 p-2 rounded-xl border-2 min-w-0 backdrop-blur-md ${selectedPlaylistIds.includes(p.playlistId) ? "border-coral bg-white/40" : "border-navy/30 bg-white/30"}`}
                  >
                    <button
                      type="button"
                      onClick={() => togglePlaylistSelection(p.playlistId)}
                      data-testid={`playlist-option-${p.playlistId}`}
                      className="flex-1 min-w-0 text-left flex items-center gap-2"
                    >
                      <span className={`w-4 h-4 rounded border-2 border-navy shrink-0 flex items-center justify-center text-[10px] font-black bg-white ${selectedPlaylistIds.includes(p.playlistId) ? "text-coral" : "text-transparent"}`}>✓</span>
                      {p.thumbnail ? <img src={p.thumbnail} alt="" className="w-10 h-10 object-cover rounded-lg border-2 border-navy shrink-0" /> : <div className="w-10 h-10 rounded-lg bg-navy shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-navy text-sm truncate">{p.title}</div>
                        <div className="text-[11px] text-navy/60 font-bold truncate">{p.itemCount} songs</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deletePlaylist(p.playlistId, p.title); }}
                      data-testid={`playlist-delete-${p.playlistId}`}
                      title="Playlist verwijderen"
                      aria-label={`Verwijder ${p.title}`}
                      className="shrink-0 w-8 h-8 rounded-lg border-2 border-navy bg-white/70 hover:bg-red-500 hover:text-white text-navy flex items-center justify-center transition-colors"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
                <div
                  className="text-xs text-white font-bold pt-2 md:col-span-2"
                  style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}
                >
                  Je hebt de mogelijkheid om 6 playlists te selecteren. {selectedPlaylistIds.length}/6 geselecteerd.
                </div>
              </div>
            )}

            {/* Playlist search — collapsible. Default: just a search icon + "Search Playlist" + chevron. */}
            <div className="mt-4 pt-4 border-t-2 border-navy/20" data-testid="playlist-search-section">
              <button
                type="button"
                onClick={() => setSearchOpen((o) => !o)}
                data-testid="playlist-search-toggle"
                aria-expanded={searchOpen}
                className="w-full flex items-center gap-2 font-bebas text-white text-xl tracking-wide"
                style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6)" }}
              >
                <Search className="w-5 h-5" />
                <span>{tr("setup.searchTitle")}</span>
                <ChevronDown className={`w-5 h-5 ml-auto transition-transform duration-300 ${searchOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Smoothly expand: 0fr -> 1fr grid trick pushes the rest of the page down. */}
              <div className={`grid transition-all duration-300 ease-out ${searchOpen ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <div className="flex gap-2">
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") runPlaylistSearch(); }}
                      placeholder={tr("setup.searchPlaceholder")}
                      data-testid="playlist-search-input"
                      className="flex-1 min-w-0 rounded-xl border-2 border-navy px-3 py-2 bg-white/80 text-navy font-bold text-sm focus:outline-none focus:border-coral"
                    />
                    <button
                      type="button"
                      onClick={runPlaylistSearch}
                      disabled={searching}
                      data-testid="playlist-search-btn"
                      className="shrink-0 rounded-xl border-2 border-navy bg-coral text-white font-display font-black px-4 py-2 hover:brightness-110 disabled:opacity-50 transition"
                    >
                      {searching ? "…" : tr("setup.searchBtn")}
                    </button>
                  </div>
                  {searchDone && searchResults.length === 0 && (
                    <div className="text-white/80 font-bold text-sm mt-2" data-testid="playlist-search-empty">{tr("setup.searchNone")}</div>
                  )}
                  {searchResults.length > 0 && (
                    <div className="space-y-2 max-h-72 overflow-auto no-scrollbar mt-3" data-testid="playlist-search-results">
                      {searchResults.map((r) => {
                        const added = playlists.some((p) => p.playlistId === r.playlistId);
                        const full = playlists.length >= 6;
                        return (
                          <div key={r.playlistId} data-testid={`search-result-${r.playlistId}`} className="card-pink-result flex items-center gap-2 p-2 rounded-2xl min-w-0">
                            {r.thumbnail ? <img src={r.thumbnail} alt="" className="w-9 h-9 object-cover rounded-lg border border-white/70 shrink-0" /> : <div className="w-9 h-9 rounded-lg bg-white/30 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-black text-white text-sm truncate" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>{r.title}</div>
                              <div className="mt-0.5">
                                <span data-testid={`search-result-count-${r.playlistId}`} className="inline-block text-[11px] font-black text-white bg-black/25 rounded-full px-2 py-0.5 border border-white/40">
                                  ♪ {r.itemCount ?? "—"} {tr("setup.songs")}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                <input readOnly value={r.url} data-testid={`search-result-url-${r.playlistId}`} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 text-[10px] text-white/90 bg-white/15 rounded px-1 py-0.5 border border-white/30 focus:outline-none" />
                                <button type="button" title={tr("setup.copyUrl")} data-testid={`search-result-copy-${r.playlistId}`} onClick={() => { navigator.clipboard?.writeText(r.url); toast.success(tr("setup.copied")); }} className="shrink-0 w-6 h-6 rounded border border-white/70 bg-white/20 text-white flex items-center justify-center hover:bg-white hover:text-coral transition">
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => addSearchResult(r)}
                              disabled={added || full || busy}
                              data-testid={`search-result-add-${r.playlistId}`}
                              title={added ? tr("setup.alreadyAdded") : tr("setup.addToSlot")}
                              className={`shrink-0 rounded-lg border-2 w-9 h-9 font-display font-black text-lg transition ${added ? "bg-teal text-white border-white/70" : full ? "bg-white/20 text-white/40 border-white/40" : "bg-white/85 text-coral border-white hover:bg-white"}`}
                            >
                              {added ? "✓" : "+"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Config */}
          <div className="card-pinkred p-5 sm:p-6 space-y-4 sm:space-y-5 min-w-0">
            <h2 className="font-bebas text-navy text-3xl sm:text-4xl tracking-wide">{tr("setup.gameConfig")}</h2>

            {(() => {
              const selected = playlists.filter((p) => selectedPlaylistIds.includes(p.playlistId));
              // Prefer server-side dedup stats when available; fall back to raw sums.
              const totalRaw = previewStats?.totalRaw ?? selected.reduce((sum, p) => sum + (p.itemCount || 0), 0);
              const unique = previewStats?.unique ?? totalRaw;
              const duplicates = previewStats?.duplicates ?? 0;
              const played = previewStats?.played ?? selected.reduce((sum, p) => sum + (p.playedCount || 0), 0);
              const remaining = previewStats?.remaining ?? Math.max(0, unique - played);
              return (
                <div
                  data-testid="playlist-song-stats"
                  className="rounded-xl border-2 border-navy p-3 flex flex-wrap items-baseline gap-x-4 gap-y-1"
                  style={{ backgroundColor: "rgba(255,255,255,0.35)" }}
                >
                  <div className="font-bebas text-navy text-lg tracking-wide">
                    {selected.length} playlist{selected.length === 1 ? "" : "s"}
                  </div>
                  <div className="font-display font-black text-navy text-base" data-testid="stats-unique">
                    <span className="opacity-60 font-bold">{tr("setup.songs")}</span> {unique}
                  </div>
                  {duplicates > 0 && (
                    <div
                      className="font-display font-black text-base"
                      style={{ color: "#7c3aed" }}
                      data-testid="stats-duplicates"
                      title={`${duplicates} dubbele songs zijn samengevoegd uit ${selected.length} playlists`}
                    >
                      <span className="opacity-70 font-bold">{tr("setup.dupRemoved")}</span> {duplicates}
                    </div>
                  )}
                  <div className="font-display font-black text-base text-white flex items-center gap-2" data-testid="stats-played" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.55)" }}>
                    <span>
                      <span className="opacity-80 font-bold">{tr("setup.alreadyPlayed")}</span> {played}
                    </span>
                    {played > 0 && (
                      <button
                        type="button"
                        onClick={() => setPendingReset(true)}
                        data-testid="stats-reset-played"
                        title="Reset de 'al gespeeld' teller voor de geselecteerde playlists"
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bebas uppercase tracking-widest rounded-full border-2 border-white/70 bg-white/15 hover:bg-white/30 text-white transition-colors"
                        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                      >
                        ↺ Reset
                      </button>
                    )}
                  </div>
                  <div className="font-display font-black text-base" style={{ color: "#4a7cab" }} data-testid="stats-remaining">
                    <span className="opacity-70 font-bold">{tr("setup.remaining")}</span> {remaining}
                  </div>
                </div>
              );
            })()}

            <div>
              <div className="font-display font-black text-navy text-sm uppercase mb-2">{tr("setup.mode")}</div>
              <div className="grid grid-cols-2 gap-2">
                {["phone", "board"].map((m) => (
                  <button
                    key={m}
                    onClick={() => {
                      if (m === "board") {
                        toast.error("Board Game modus is nog niet beschikbaar — komt binnenkort!", { duration: 4000 });
                        return;
                      }
                      setMode(m);
                    }}
                    data-testid={`mode-${m}`}
                    className={`py-2 rounded-lg border-2 border-navy font-display font-black uppercase text-sm ${mode === m ? "bg-navy text-cream" : "bg-white/40 backdrop-blur-sm"} ${m === "board" ? "opacity-60 cursor-not-allowed" : ""}`}
                    title={m === "board" ? "Nog niet beschikbaar" : undefined}
                  >
                    {m === "phone" ? tr("setup.phone") : tr("setup.boardLocked")}
                  </button>
                ))}
              </div>
              {mode === "board" && (
                <div
                  data-testid="board-unavailable-notice"
                  className="mt-2 text-xs font-bold px-3 py-2 rounded-lg border-2"
                  style={{ backgroundColor: "rgba(230, 91, 124, 0.15)", borderColor: "#e65b7c", color: "#7f1d1d" }}
                >
                  🚧 Board Game modus is nog niet beschikbaar. We werken eraan — kies voorlopig <b>Phone</b>.
                </div>
              )}
            </div>

            <div>
              <div className="font-display font-black text-navy text-sm uppercase mb-2">{tr("setup.gameTypes")} <span className="normal-case text-navy/60 text-xs font-bold">{tr("setup.gameTypesHint")}</span></div>
              <div className="space-y-2">
                {[1, 2, 3, 4].map((t) => {
                  if (mode === "board" && t !== 1) return null;
                  const info = GAME_TYPE_INFO[t];
                  const selected = gameTypes.includes(t);
                  const locked = t === 3 || t === 4; // Mosaic & Videoclip — Pro version
                  if (locked) {
                    return (
                      <div key={t} className="relative">
                        <button
                          type="button"
                          disabled
                          aria-disabled="true"
                          data-testid={`gametype-${t}`}
                          title="Coming soon in the Pro version"
                          className="w-full p-3 pr-11 rounded-lg border-2 border-navy/40 text-left flex items-center gap-3 bg-white/25 backdrop-blur-sm opacity-70 cursor-not-allowed"
                        >
                          <Lock className="w-5 h-5 text-navy/60 shrink-0" strokeWidth={2.5} />
                          <span className="flex-1 font-display font-black text-navy/70">{tr(`gt.${t}`)}</span>
                        </button>
                        <span
                          data-testid={`gametype-locked-badge-${t}`}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bebas uppercase tracking-widest bg-navy text-cream border-2 border-cream"
                          title="Coming soon in the Pro version"
                        >
                          <Lock className="w-3 h-3" strokeWidth={3} /> Pro
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div key={t} className="relative">
                      <button
                        onClick={() => toggleGameType(t)}
                        data-testid={`gametype-${t}`}
                        className={`w-full p-3 pr-11 rounded-lg border-2 border-navy text-left flex items-center gap-3 ${selected ? "text-white" : "bg-white/40 backdrop-blur-sm"}`}
                        style={selected ? { backgroundColor: info.color } : undefined}
                      >
                        <span className={`w-5 h-5 rounded border-2 border-current shrink-0 flex items-center justify-center text-xs font-black`}>
                          {selected ? "✓" : ""}
                        </span>
                        <span className="flex-1 font-display font-black">{tr(`gt.${t}`)}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setInfoModalGt(t); }}
                        onMouseEnter={() => setInfoModalGt(t)}
                        onFocus={() => setInfoModalGt(t)}
                        data-testid={`gametype-info-${t}`}
                        aria-label={`Meer info over ${info.title}`}
                        title="Meer info"
                        className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 border-navy flex items-center justify-center transition-transform hover:scale-110 ${selected ? "bg-white/90 text-navy" : "bg-white text-navy"}`}
                      >
                        <HelpCircle className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    </div>
                  );
                })}
                {!mode || mode === "phone" ? (
                  <div
                    data-testid="gametype-locked-hint"
                    className="text-xs text-white font-bold px-3 py-1.5 rounded-lg border-2 border-white/50 flex items-center gap-1.5"
                    style={{ backgroundColor: "rgba(0,0,0,0.18)", textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}
                  >
                    <Lock className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} /> {tr("setup.mosaicSoon")}
                  </div>
                ) : null}
                {mode === "board" && (
                  <div
                    data-testid="board-only-notice"
                    className="text-xs text-white font-bold px-3 py-2 rounded-lg border-2 border-white/60"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.15)",
                      textShadow: "0 1px 4px rgba(0,0,0,0.55)",
                    }}
                  >
                    🔒 In Board Game mode is alleen <b>Raad de artiest</b> beschikbaar. Wissel naar Phone mode voor de andere game types.
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="font-display font-black text-navy text-sm uppercase mb-2">Muziek speelt op</div>
              <div className="grid grid-cols-2 gap-2" data-testid="audio-source-toggle">
                {[
                  { key: "host", icon: "🖥️", label: "Host apparaat", sub: "Alle spelers luisteren mee" },
                  { key: "players", icon: "📱", label: "Speler apparaten", sub: "Speel op afstand", locked: true },
                ].map((opt) => {
                  const selected = audioSource === opt.key;
                  if (opt.locked) {
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        disabled
                        aria-disabled="true"
                        data-testid={`audio-source-${opt.key}`}
                        title="Coming soon in the Pro version"
                        className="relative p-3 rounded-lg border-2 border-navy/40 text-left flex items-start gap-2 bg-white/25 backdrop-blur-sm text-navy/70 opacity-70 cursor-not-allowed"
                      >
                        <Lock className="w-5 h-5 shrink-0 mt-0.5 text-navy/60" strokeWidth={2.5} />
                        <span className="flex-1">
                          <div className="font-display font-black leading-tight">{opt.icon} {opt.label}</div>
                          <div className="text-xs opacity-80 leading-tight">{opt.sub}</div>
                        </span>
                        <span
                          data-testid="audio-source-locked-badge"
                          className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bebas uppercase tracking-widest bg-navy text-cream border-2 border-cream"
                        >
                          <Lock className="w-3 h-3" strokeWidth={3} /> Pro
                        </span>
                      </button>
                    );
                  }
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setAudioSource(opt.key)}
                      data-testid={`audio-source-${opt.key}`}
                      className={`p-3 rounded-lg border-2 border-navy text-left flex items-start gap-2 transition-colors ${selected ? "text-white" : "bg-white/40 backdrop-blur-sm text-navy"}`}
                      style={selected ? { backgroundColor: "#0f766e" } : undefined}
                    >
                      <span className={`w-5 h-5 rounded-full border-2 border-current shrink-0 mt-0.5 flex items-center justify-center text-xs font-black`}>
                        {selected ? "●" : ""}
                      </span>
                      <span className="flex-1">
                        <div className="font-display font-black leading-tight">{opt.icon} {opt.label}</div>
                        <div className="text-xs opacity-80 leading-tight">{opt.sub}</div>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div
                data-testid="audio-source-locked-hint"
                className="mt-2 text-xs text-white font-bold px-3 py-1.5 rounded-lg border-2 border-white/50 flex items-center gap-1.5"
                style={{ backgroundColor: "rgba(0,0,0,0.18)", textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}
              >
                <Lock className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} /> Muziek op telefoon: coming soon in the Pro version
              </div>
            </div>

            <div>
              <div className="font-display font-black text-navy text-sm uppercase mb-2">Rounds: <span style={{ color: "#e65b7c" }}>{rounds}</span></div>
              <input type="range" min="3" max="50" value={rounds} onChange={(e) => setRounds(+e.target.value)}
                     data-testid="rounds-slider" className="w-full" style={{ accentColor: "#e65b7c" }} />
            </div>

            {!roomCode ? (
              <button data-testid="host-create-room-btn" onClick={createRoom}
                className="w-full text-white font-bebas text-xl uppercase tracking-wider py-3 rounded-2xl border-2 border-black shadow-[0_8px_22px_rgba(0,0,0,0.3)] hover:translate-y-[-2px] transition"
                style={{ backgroundColor: "#e65b7c" }}>
                {tr("setup.createRoom")}
              </button>
            ) : (
              <div className="text-center flex flex-col items-center gap-2">
                <div className="text-navy/70 font-bold text-xs uppercase tracking-widest">{tr("setup.roomCode")}</div>
                <div data-testid="room-code-display" className="font-display font-black text-navy text-4xl tracking-widest">{roomCode}</div>
                <div className="mx-auto aspect-square w-44 max-w-[70vw] bg-white border-2 border-black rounded-2xl flex items-center justify-center p-3 shadow-[0_4px_12px_rgba(0,0,0,0.15)]">
                  <QRCodeSVG
                    value={`${window.location.origin}/join?code=${roomCode}`}
                    bgColor="#FFFFFF"
                    fgColor="#1E1B4B"
                    style={{ width: "100%", height: "100%", display: "block" }}
                  />
                </div>
                <div className="text-navy/60 font-bold mt-1">{players.length} {tr("setup.joinedSuffix")}</div>
              </div>
            )}

            <button
              data-testid="host-start-game-button"
              onClick={() => { const missing = collectValidationErrors(); if (missing.length) { setValidationModal({ missing }); return; } setConfirmStart(true); }}
              disabled={busy || selectedPlaylistIds.length === 0 || players.length < 1}
              className="w-full text-white font-bebas text-3xl uppercase tracking-wider py-4 rounded-2xl border-2 border-black shadow-[0_10px_28px_rgba(0,0,0,0.35)] hover:translate-y-[-2px] active:translate-y-[2px] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#e65b7c" }}
            >
              ▶ Start Game
            </button>
            {roomCode && players.length < 1 && (
              <div data-testid="host-waiting-ready-hint" className="text-center text-white/90 font-bold text-xs mt-2" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                Wacht tot minstens 1 speler is toegetreden…
              </div>
            )}
            {roomCode && players.length >= 1 && (
              <div data-testid="host-ready-count" className={`text-center font-bold text-xs mt-2 ${readyCount >= players.length ? "text-emerald-300" : "text-white"}`} style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                {readyCount >= players.length
                  ? `✓ Alle ${players.length} speler${players.length === 1 ? "" : "s"} klaar!`
                  : `${players.length} speler${players.length === 1 ? "" : "s"} toegetreden · ${readyCount} klaar ✓`}
              </div>
            )}
          </div>
        </div>

        {/* Players roster */}
        {roomCode && (
          <div className="mt-6 tactile p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bebas text-navy text-2xl sm:text-3xl tracking-wide">{tr("setup.playersInLobby")}</h3>
              <div className="text-navy/60 font-bold text-sm">{tr("setup.max8")}</div>
            </div>
            {players.length === 0 ? (
              <div className="text-navy/60 font-bold">{tr("setup.waitingForPlayers")} <span className="text-coral font-black">{roomCode}</span>…</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {players.map((p) => (
                  <div key={p.id} data-testid={`lobby-player-${p.id}`} className={`border-2 rounded-lg px-3 py-2 backdrop-blur-sm font-display font-black truncate flex items-center gap-2 ${p.ready ? "border-emerald-600 bg-emerald-50/80 text-emerald-900" : "border-navy bg-white/50 text-navy"}`}>
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] shrink-0 ${p.ready ? "bg-emerald-600 text-white" : "bg-navy/20 text-navy"}`}>{p.ready ? "✓" : "…"}</span>
                    <span className="truncate">{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation modal for missing playlist / game type */}
      <AlertDialog open={!!validationModal} onOpenChange={(o) => { if (!o) setValidationModal(null); }}>
        <AlertDialogContent data-testid="host-validation-dialog" className="border-4 border-navy bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bebas tracking-wide text-3xl text-navy">
              {tr("setup.almostReady")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="font-poppins text-navy" style={{ fontWeight: 300, fontSize: "12pt" }}>
                {tr("setup.beforeCreate")}
                <ul className="mt-3 space-y-2 list-none">
                  {validationModal?.missing?.includes("playlist") && (
                    <li className="flex items-center gap-2" data-testid="validation-missing-playlist">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white font-black text-xs shrink-0">!</span>
                      <span><b style={{ fontWeight: 700 }}>{tr("setup.noPlaylistSelected")}</b> {tr("setup.pickMin1")}</span>
                    </li>
                  )}
                  {validationModal?.missing?.includes("gametype") && (
                    <li className="flex items-center gap-2" data-testid="validation-missing-gametype">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white font-black text-xs shrink-0">!</span>
                      <span><b style={{ fontWeight: 700 }}>{tr("setup.noGameTypeSelected")}</b> {tr("setup.pickMin1")}</span>
                    </li>
                  )}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              data-testid="validation-ok-btn"
              onClick={() => setValidationModal(null)}
              className="font-bebas uppercase tracking-widest text-white"
              style={{ backgroundColor: "#e65b7c" }}
            >
              {tr("setup.okSelect")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm start — "Are all players ready to begin?" */}
      <AlertDialog open={confirmStart} onOpenChange={(o) => { if (!o) setConfirmStart(false); }}>
        <AlertDialogContent data-testid="host-confirm-start-dialog" className="border-4 border-navy bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bebas tracking-wide text-3xl text-navy">
              {tr("setup.allReadyQ")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="font-poppins text-navy" style={{ fontWeight: 300, fontSize: "12pt" }}>
                <b style={{ fontWeight: 700 }}>{readyCount}</b> {tr("setup.confirmOf")} <b style={{ fontWeight: 700 }}>{players.length}</b> {tr("setup.confirmPlayerWord")}{players.length === 1 ? "" : "s"} {tr("setup.confirmTapped")}
                {readyCount < players.length ? (
                  <div data-testid="host-confirm-not-all-ready" className="mt-3 rounded-lg border-2 border-amber-500 bg-amber-50 px-3 py-2 text-amber-800 font-bold text-sm">
                    ⏳ {players.length - readyCount} speler{players.length - readyCount === 1 ? "" : "s"} is nog niet klaar. Je kunt wachten of nu forceren.
                  </div>
                ) : (
                  <div data-testid="host-confirm-all-ready" className="mt-3 rounded-lg border-2 border-emerald-600 bg-emerald-50 px-3 py-2 text-emerald-800 font-bold text-sm">
                    ✓ Iedereen is klaar — veel plezier!
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="host-confirm-start-cancel" className="font-bebas uppercase tracking-widest border-2 border-navy text-navy">
              {tr("setup.waitLonger")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="host-confirm-start-btn"
              onClick={() => { setConfirmStart(false); startGame(); }}
              className="font-bebas uppercase tracking-widest text-white"
              style={{ backgroundColor: "#e65b7c" }}
            >
              {readyCount < players.length ? "Nu starten (forceren)" : "Ja, start het spel!"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete playlist confirmation */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="playlist-delete-dialog" className="border-4 border-navy bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bebas tracking-wide text-3xl text-navy">
              Playlist verwijderen?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-poppins text-navy" style={{ fontWeight: 300, fontSize: "12pt" }}>
              Weet je zeker dat je de playlist <b style={{ fontWeight: 700 }}>{pendingDelete?.title}</b> wilt verwijderen? Dit kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="playlist-delete-cancel" className="font-bebas uppercase tracking-widest">
              Annuleer
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="playlist-delete-confirm"
              onClick={confirmDeletePlaylist}
              className="font-bebas uppercase tracking-widest text-white"
              style={{ backgroundColor: "#dc2626" }}
            >
              Verwijder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset 'al gespeeld' confirmation */}
      <AlertDialog open={pendingReset} onOpenChange={(o) => { if (!o) setPendingReset(false); }}>
        <AlertDialogContent data-testid="reset-played-dialog" className="border-4 border-navy bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-bebas tracking-wide text-3xl text-navy">
              Reset 'al gespeeld'?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-poppins text-navy" style={{ fontWeight: 300, fontSize: "12pt" }}>
              De teller voor <b style={{ fontWeight: 700 }}>{selectedPlaylistIds.length} geselecteerde playlist{selectedPlaylistIds.length === 1 ? "" : "s"}</b> wordt gereset naar 0. Alle nummers zijn hierna weer beschikbaar in nieuwe games.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-played-cancel" className="font-bebas uppercase tracking-widest">
              Annuleer
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-played-confirm"
              onClick={confirmResetPlayed}
              className="font-bebas uppercase tracking-widest text-white"
              style={{ backgroundColor: "#7c3aed" }}
            >
              Reset teller
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Game type info modal — background matches the game type card color */}      <Dialog open={!!infoModalGt} onOpenChange={(o) => { if (!o) setInfoModalGt(null); }}>
        <DialogContent
          data-testid="gametype-info-modal"
          onMouseLeave={() => setInfoModalGt(null)}
          className="max-w-md border-4 border-navy p-0 overflow-hidden"
          style={{ backgroundColor: infoModalGt ? GAME_TYPE_INFO[infoModalGt].color : "#4a7cab" }}
        >
          {infoModalGt && (() => {
            const info = GAME_TYPE_INFO[infoModalGt];
            return (
              <div className="p-6 sm:p-7 text-white">
                <DialogHeader>
                  <DialogTitle className="font-bebas tracking-wide text-3xl sm:text-4xl text-white text-left leading-tight">
                    {info.title}
                  </DialogTitle>
                </DialogHeader>
                <div
                  className="mt-1 mb-4 inline-block px-3 py-1 rounded-full text-xs font-bebas uppercase tracking-widest bg-white/25 border-2 border-white/40"
                  data-testid="gametype-info-mode"
                >
                  {info.modeLabel}
                </div>
                <p
                  className="font-poppins text-white/95 mb-4"
                  style={{ fontWeight: 300, fontSize: "11pt", lineHeight: 1.55 }}
                >
                  {info.description}
                </p>
                <div className="rounded-xl border-2 border-white/40 bg-white/10 backdrop-blur-sm p-3">
                  <div className="font-bebas text-white text-lg tracking-widest mb-2 uppercase">Puntenverdeling</div>
                  <ul className="space-y-1.5">
                    {info.points.map((p) => (
                      <li key={p.label} className="flex items-baseline justify-between font-display gap-3">
                        <span className="text-white/90" style={{ fontWeight: 400 }}>{p.label}</span>
                        <span className="font-black text-white text-lg">{p.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => setInfoModalGt(null)}
                  data-testid="gametype-info-close"
                  className="mt-5 w-full bg-white text-navy font-bebas uppercase tracking-widest py-2.5 rounded-lg border-2 border-navy hover:translate-y-[-2px] transition-transform"
                >
                  Sluiten
                </button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
